//#region IMPORTS
import {
  SortingState,
  useReactTable,
  getCoreRowModel,
  PaginationState,
  getSortedRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getFacetedUniqueValues,
} from "@tanstack/react-table";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Order } from "../../models/order";
import PaginationBarClient from "../paginationBar/PaginationBarClient";
import MainSortableTable from "../_layout/MainSortableTable";
//#endregion

//#region INTERFACE
interface Props {
  selectOrdersHandler: (selectedOrders: Order[]) => void;
  loading?: boolean;
  loaded?: boolean;
  columns: any;
  orders: Order[];
}
//#endregion

export default function OrdersHandlingTable({
  selectOrdersHandler,
  columns,
  loading = true,
  loaded = false,
  orders,
}: Props) {
  //#region SETUP
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  //#endregion

  //#region LOGIC
  const selectedRows = useMemo(() => {
    return orders.reduce((acc, order) => {
      if (order.selected) {
        acc[order.id] = true;
      }
      return acc;
    }, {} as Record<number, boolean>);
  }, [orders]);

  useEffect(() => {
    setRowSelection(selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    const selectedOrders = orders.filter((order) =>
      Object.keys(rowSelection)
        .map((key) => Number(key))
        .includes(order.id)
    );
    selectOrdersHandler(selectedOrders);
  }, [rowSelection, orders]);

  // Reset to first page when filters change
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);
  //#endregion

  //#region REACT TABLE
  const table = useReactTable({
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id.toString(),
    enableRowSelection: true,
    columns,
    state: { rowSelection, pagination, sorting },
    data: orders,
  });
  //#endregion

  //#region RENDER

  if (loaded && orders.length <= 0)
    return (
      <div className="min-w-full mt-16 py-6 px-4 sm:px-6 lg:px-8">
        No orders found...
      </div>
    );

  return (
    <Fragment>
      <MainSortableTable
        loading={loading}
        table={table}
        changeHoverBGColor={false}
      />
      <PaginationBarClient
        changePageSizeHandler={(pageSize: number) =>
          table.setPageSize(pageSize)
        }
        prevPageAvailable={table.getCanPreviousPage()}
        nextPageAvailable={table.getCanNextPage()}
        firstPageHandler={() => table.firstPage()}
        prevPageHandler={() => table.previousPage()}
        nextPageHandler={() => table.nextPage()}
        lastPageHandler={() => table.lastPage()}
        totalAmmount={table.getRowCount()}
        currentPage={table.getState().pagination.pageIndex + 1}
        pageCount={table.getPageCount()}
        disabled={loading}
        pageSize={table.getState().pagination.pageSize}
        type="orders"
      />
    </Fragment>
  );

  //#endregion
}
